// client/src/pages/admin/AdminForgotPassword.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminAuthLayout from '../../components/admin/layout/AdminAuthLayout';
import '../../styles/components/admin/AdminAuth.css';
import neuroLexLogo from '../../assets/images/Neurolex_Logo_New.png';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const AdminForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleChange = (e) => {
    setEmail(e.target.value);
    if (emailError) setEmailError('');
    if (error) setError('');
    if (message) setMessage('');
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return "Email is required";
    if (!regex.test(email)) return "Please enter a valid email address";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const emailValidationError = validateEmail(email);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      return;
    }
    
    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      console.log('🔧 Requesting password reset for:', email);
      
      const response = await axios.post(`${API_URL}/admin/forgot-password`, { email });
      
      console.log('✅ Password reset response:', response.data);
      
      if (response.data.success) {
        setEmailSent(true);
        setMessage('Password reset instructions have been sent to your email.');
      } else {
        setError(response.data.message || 'Failed to send reset email');
      }
    } catch (error) {
      console.error('❌ Forgot password error:', error);
      
      if (error.response) {
        const errorMessage = error.response.data?.message || 'Unable to process request';
        setError(errorMessage);
      } else if (error.request) {
        setError('Network error. Please check your connection.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const forgotPasswordForm = (
    <div className="form-container">
      <div className="form-content-container">
        <img 
          src={neuroLexLogo}
          alt="Neurolex" 
          className="app-logo-small" 
        />
        
        <h1 className="form-title">FORGOT PASSWORD</h1>
        
        {!emailSent ? (
          <>
            <p className="form-subtitle">
              Enter your email address and we'll send you instructions to reset your password.
            </p>
            
            {error && (
              <div className="alert alert-error">
                {error}
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              <div className="admin-form-group">
                <label htmlFor="email" className="admin-form-label">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="admin@neurolex.com"
                  value={email}
                  onChange={handleChange}
                  className={`admin-form-input ${emailError ? 'input-error' : ''}`}
                />
                {emailError && <div className="error-message">{emailError}</div>}
              </div>
              
              <button 
                type="submit" 
                className="admin-form-button" 
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="loading-spinner"></span>
                ) : (
                  'Send Reset Instructions'
                )}
              </button>
              
              <div className="account-link-container">
                <span className="account-link-text">Remember your password?</span>
                <Link to="/admin/login" className="account-link">Back to Login</Link>
              </div>
            </form>
          </>
        ) : (
          <>
            <div className="alert alert-success">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '10px' }}>
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
              {message}
            </div>
            
            <p className="form-subtitle" style={{ marginTop: '20px' }}>
              Please check your email inbox and follow the instructions to reset your password. 
              The link will expire in 1 hour.
            </p>
            
            <div className="account-link-container" style={{ marginTop: '30px' }}>
              <span className="account-link-text">Didn't receive the email?</span>
              <button 
                onClick={() => {
                  setEmailSent(false);
                  setMessage('');
                }}
                className="account-link"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                Try Again
              </button>
            </div>
            
            <div className="account-link-container">
              <Link to="/admin/login" className="account-link">Back to Login</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <AdminAuthLayout>
      {forgotPasswordForm}
    </AdminAuthLayout>
  );
};

export default AdminForgotPassword;